import { useEffect, useState } from 'react';

export interface DsstSpickMetrics {
  height: number;
  guidanceHeight: number;
  screenPb: number;
  symbolSize: number;
  mapSymbol: number;
}

// 게임 화면 좌우 패딩 (px-5)
const HORIZONTAL_PADDING = 40;
// 컨텐츠 최대 폭
const MAX_APP_WIDTH = 430;
// 게임판 하단 최소 여백 (12~16px 확보)
const MIN_BOTTOM_GAP = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// 실제 사용 가능한 viewport 높이를 읽어 DSST(스픽) 게임 영역 지표를 계산한다.
// 브라우저 주소창/하단 메뉴 등 UI 변화는 visualViewport.height 로 감지하고,
// 100dvh 를 fallback 으로 사용해 주소창이 접히고 펼쳐져도 레이아웃이 깨지지 않게 한다.
function computeMetrics(): DsstSpickMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 390;
  const appWidth = Math.min(viewportWidth, MAX_APP_WIDTH);

  let guidanceHeight: number;
  let symbolSize: number;
  let mapSymbol: number;

  if (height >= 780) {
    guidanceHeight = 76;
    symbolSize = 104;
    mapSymbol = 30;
  } else if (height >= 660) {
    guidanceHeight = 68;
    symbolSize = 92;
    mapSymbol = 28;
  } else if (height >= 560) {
    guidanceHeight = 58;
    symbolSize = 80;
    mapSymbol = 24;
  } else {
    guidanceHeight = 52;
    symbolSize = 68;
    mapSymbol = 22;
  }

  // 좁은 화면에서는 현재 기호가 게임판 밖으로 넘치지 않게 폭 기준으로 한 번 더 제한
  const maxSymbolFromWidth = Math.max(60, Math.round((appWidth - HORIZONTAL_PADDING - 64) * 0.55));
  symbolSize = clamp(symbolSize, 60, maxSymbolFromWidth);

  return {
    height,
    guidanceHeight,
    screenPb: Math.max(MIN_BOTTOM_GAP, height >= 780 ? 20 : MIN_BOTTOM_GAP),
    symbolSize,
    mapSymbol,
  };
}

function applyMetrics(m: DsstSpickMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--sh-app-height', `${m.height}px`);
  root.setProperty('--sh-guidance-height', `${m.guidanceHeight}px`);
  root.setProperty('--sh-screen-pb', `${m.screenPb}px`);
  root.setProperty('--dsst-symbol-size', `${m.symbolSize}px`);
  root.setProperty('--dsst-map-symbol', `${m.mapSymbol}px`);
}

export function useViewportMetrics(): DsstSpickMetrics {
  const [metrics, setMetrics] = useState<DsstSpickMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}